<template>
  <div class="relative w-full">
      <div class="flex items-stretch h-[2.75rem] w-[400px] border border-border-basic-2 rounded-03 overflow-hidden bg-white" style="margin:1.5rem auto 0 auto">
        <!-- ◀ 이전 -->
        <button
            :disabled="pickerMode === 'manual'"
            @click="changeSearchDate(-1)"
            class="w-[2.75rem] flex items-center justify-center border-r border-border-basic-2  hover:bg-background-basic-3"
            :class="pickerMode === 'manual'
            ? 'cursor-not-allowed opacity-40 hover:bg-background-basic-1'
            : 'hover:bg-background-basic-1'"
        >
            <ArrowDropdownIcon :width="9" :height="6" weight="THIN" color="text-content-basic-2" class="rotate-90"/>
        </button>
        <!-- 중앙 트리거 -->
        <div class="flex-1 flex items-center justify-center font-body-sm-regular text-center cursor-pointer select-none" @click="openDatePicker">
          {{ displayRange }}
        </div>
        <!-- ▶ 다음 -->
        <button
            :disabled="pickerMode === 'manual'"
            @click="changeSearchDate(1)"
            class="w-[2.75rem] flex items-center justify-center border-l border-border-basic-2 hover:bg-background-basic-3"
            :class="pickerMode === 'manual'
            ? 'cursor-not-allowed opacity-40 hover:bg-background-basic-1'
            : 'hover:bg-background-basic-1'"
        >
            <ArrowDropdownIcon :width="9" :height="6" weight="THIN" color="text-content-basic-2" style="transform: rotate(270deg);"/>
        </button>
      </div>

    <!-- DatePicker -->
    <VueDatePicker
      ref="datePicker"
      v-model="internalValue"
      :week-picker="pickerMode === 'weekly'"
      :month-picker="pickerMode === 'monthly'"
      :range="pickerMode === 'manual'"
      :auto-apply="true"
      :enable-time-picker="false"
      :clearable="false"
      :hide-input-icon="true"
      :max-date="manualMaxDate"
      :min-date="manualMinDate"
      input-class="dp-hidden-input"
      teleport
      class="custom-datepicker"
      @update:model-value="onUpdate"
      @internal-model-change="onInternalChange"
    >
      <template #left-sidebar>
         <button
            v-for="mode in modes"
            :key="mode.value"
            @click="onClickMode(mode.value)"
            class="
                flex w-[130px] h-[44px]
                px-2
                justify-center items-center gap-1
                rounded-lg
                text-[14px]
                font-medium
                leading-normal
                text-center
                font-inter
                transition
                my-2
            "
            :class="pickerMode === mode.value
            ? 'bg-[#112FBA] text-white'
            : 'bg-white text-gray-500 hover:bg-gray-50'"
        >
            {{ mode.label }}
        </button>
      </template>
    </VueDatePicker>
  </div>
</template>

<script>
import '@vuepic/vue-datepicker/dist/main.css';
import ArrowDropdownIcon from '@/components/design-system/icon/arrow-drop-down.vue'

import VueDatePicker from '@vuepic/vue-datepicker';
import moment from 'moment-timezone';
export default {
    components: { VueDatePicker, ArrowDropdownIcon },

    props: {
        startDate: Date,
        endDate: Date,
    },

    emits: ['update:startDate', 'update:endDate', 'searchEvent'],

    data() {
        return {
            pickerMode: 'weekly',
            internalValue: null,
            manualStartDate: null,
            manualEndDate: null,
            MAX_RANGE_DAYS: 364,
            isChangingMode: false,
            isUserUpdating: false,
            modes: [
                { label: 'Weekly', value: 'weekly' },
                { label: 'Monthly', value: 'monthly' },
                { label: 'Manual Select', value: 'manual' }
            ]
        }
    },
    computed: {
        displayRange() { 
            if (!(this.startDate instanceof Date)) return ''
            if (!(this.endDate instanceof Date)) return ''
            return `${this.formatDate(this.startDate)} ~ ${this.formatDate(this.endDate)}`
        },
        manualBaseDate() {
            return this.manualStartDate || this.manualEndDate || null
        },

        manualMaxDate() {
            if (this.pickerMode !== 'manual') return null
          
            const today = new Date()
            today.setHours(23, 59, 59, 999)

            if (!this.manualBaseDate) return today

            const max = new Date(this.manualBaseDate)
            max.setDate(max.getDate() + this.MAX_RANGE_DAYS)
            max.setHours(23, 59, 59, 999)

            return max < today ? max : today
        },

        manualMinDate() {
            if (this.pickerMode !== 'manual') return null
            if (!this.manualBaseDate) return null

            const min = new Date(this.manualBaseDate)
            min.setDate(min.getDate() - this.MAX_RANGE_DAYS)
            min.setHours(0, 0, 0, 0)

            return min
        }
    },

    mounted() {
        this.setMode('weekly', { open: false }) 
    },

    watch: {
        startDate(start) {
            if (this.pickerMode === 'manual') return
            if (
                this.isChangingMode ||
                this.isUserUpdating ||
                !(start instanceof Date) ||
                !(this.endDate instanceof Date)
            ) return

            this.internalValue =
                this.pickerMode === 'monthly'
                ? { year: start.getFullYear(), month: start.getMonth() }
                : [this.startDate, this.endDate]
        },
        pickerMode(newMode, oldMode) {
            if (newMode === 'manual' && oldMode !== 'manual') {
                this.resetManualState()
            }
        }
    },
    methods: {
        resetManualStateAfterSelect() {
             this.$nextTick(() => {
                this.resetManualState()
            })
        },
        resetManualState() {
            this.manualStartDate = null
            this.manualEndDate   = null
            this.internalValue   = null

        },
         normalizeDate(date) {
            const d = new Date(date)
            d.setHours(0, 0, 0, 0)
            return d
        },
         changeSearchDateType(type) {
            const self = this;
            self.searchDateType = type;

            if (type === 'WEEKLY') {
                self.weekOffset = 0;
                self.getPeriodOfWeek();
            } else if (type === 'MONTHLY') {
                self.monthOffset = 0;
                self.getPeriodOfMonth();
            } else if (type === 'MANUAL') {
                self.getPeriodOfManual();
            }
        },
         getPeriodOfWeek(addWeekNum = 0) {
            const self = this;

            self.periodStart = moment();

            self.periodStart.add(addWeekNum, 'w');

            let clonePeriodStart = self.periodStart.clone();
            let clonePeriodStart2 = self.periodStart.clone();

            self.periodStart = clonePeriodStart.startOf('isoWeek');
            self.periodEnd = clonePeriodStart2.endOf('isoWeek');

            self.searchDateText = self.getSearchDate(self.periodStart, self.periodEnd);

            const start = self.periodStart.format('YYYYMMDD');
            const end = self.periodEnd.format('YYYYMMDD');

            self.$emit('update:startDate', start);
            self.$emit('update:endDate', end);
            self.$emit('searchEvent');
        },
        getPeriodOfMonth(addMonthNum = 0) {
            const self = this;

            self.periodStart = moment();

            self.periodStart.add(addMonthNum, 'M').date(1);
            let clonePeriodStart = self.periodStart.clone();
            self.periodEnd = clonePeriodStart.endOf('month');

            self.searchDateText = self.getSearchDate(self.periodStart, self.periodEnd);

            const start = self.periodStart.format('YYYYMMDD');
            const end = self.periodEnd.format('YYYYMMDD');

            self.$emit('update:startDate', start);
            self.$emit('update:endDate', end);
            self.$emit('searchEvent');
        },
        getPeriodOfManual() {
            // const self = this;
            // self.searchDateText = null;
        },
         getSearchDate(start, end) {
            const self = this;
            if (!this.weekDay) return ''

            const startDay = this.weekDay[start.day()] ?? ''
            const endDay = this.weekDay[end.day()] ?? ''

            return `${start.format('MM/DD/YYYY')}(${startDay}) - ${end.format('MM/DD/YYYY')}(${endDay})`
        },
        /* ======================
        *  Date Click Check
        * ====================== */
        onInternalChange(value) {
            if (this.pickerMode !== 'manual') return
            if (!Array.isArray(value)) return

            const [start, end] = value

            if (start instanceof Date && !end) {            
                if (!this.manualStartDate && !this.manualEndDate) {
                    this.manualStartDate = start
                }
            }

            if (start instanceof Date && end instanceof Date) {
                this.manualStartDate = start
                this.manualEndDate   = end
            }
        },
        /* ======================
        * MODE CONTROL
        * ====================== */
        onClickMode(mode) {
            if (this.pickerMode !== mode) this.setMode(mode)
        },

        setMode(mode, { open = true } = {}) {
            this.isChangingMode = true
            this.pickerMode = mode

            const today = new Date()
            let start, end
                
            if (mode === 'weekly') {
                start = this.getWeekStart(today)
                end = this.getWeekEnd(start)
                this.internalValue = [start, end]
            }

            if (mode === 'monthly') {
                const year = today.getFullYear()
                const month = today.getMonth()
                start = new Date(year, month, 1)
                end = new Date(year, month + 1, 0)
                this.internalValue = { year, month }
            }

            if (mode === 'manual') {
                this.internalValue = null 
                this.manualStartDate = null 
            }

            this.$nextTick(() => {
                if (open) this.$refs.datePicker.openMenu()
                this.isChangingMode = false
            })
        },
        onUpdate(value) {
            this.isUserUpdating = true
            
            if (this.pickerMode === 'monthly') {
                if (!value || value.year == null || value.month == null) {
                    this.isUserUpdating = false
                    return
                }
                
                const start = this.normalizeDate(
                    new Date(value.year, value.month, 1)
                )
                const end = this.normalizeDate(
                    new Date(value.year, value.month + 1, 0)
                )
                this.emitChange(start, end)
                this.$refs.datePicker.closeMenu()
                this.isUserUpdating = false
                return
            }

            if (this.pickerMode === 'weekly') {
                 let start

                if (value instanceof Date) {
                start = value
                } else if (Array.isArray(value) && value[0] instanceof Date) {
                start = value[0]
                } else {
                this.isUserUpdating = false
                return
                }

                start = this.normalizeDate(start)

                const end = new Date(start)
                end.setDate(start.getDate() + 6)
                this.emitChange(start, end)
                this.isUserUpdating = false
                return
            }

            if (this.pickerMode === 'manual') {
                if (!Array.isArray(value)) {
                    this.isUserUpdating = false
                    return
                }

                let [start, end] = value
                if (!(start instanceof Date) || !(end instanceof Date)) return

                start = this.normalizeDate(start)
                end   = this.normalizeDate(end)

                const today = new Date()
                today.setHours(23, 59, 59, 999)

                if (end > today) end = today

                const  diffDays = (end - start) / 86400000

                if (diffDays > this.MAX_RANGE_DAYS) {
                    start = new Date(end)
                    start.setDate(start.getDate() - this.MAX_RANGE_DAYS)
                }
               
                this.resetManualStateAfterSelect()
                this.emitChange(start, end)
                this.isUserUpdating = false
            }
        },
        /* ============================
        * NAVIGATION (weekly , monthly)
        * ============================= */
        changeSearchDate(step) {  
            if (this.pickerMode === 'manual') return

            const base = this.startDate ?? new Date()
            let start, end

            if (this.pickerMode === 'monthly') {
                start = new Date(base.getFullYear(), base.getMonth() + step, 1)
                end   = new Date(start.getFullYear(), start.getMonth() + 1, 0)
            } else {
                start = new Date(this.startDate)
                start.setDate(start.getDate() + step * 7)
                end = new Date(start)
                end.setDate(start.getDate() + 6)
            }

            this.internalValue = [start, end]
            this.emitChange(start, end)
        },
        openDatePicker() {
            if (this.pickerMode === 'monthly' && this.startDate instanceof Date) {
                this.internalValue = {
                    year: this.startDate.getFullYear(),
                    month: this.startDate.getMonth(),
                }
            }
            this.$refs.datePicker.openMenu()
        },

        /* ======================
        * UTIL
        * ====================== */

        emitChange(start, end) {
            this.$emit('update:startDate', start)
            this.$emit('update:endDate', end)
            this.$emit('searchEvent')
        },
        getWeekStart(date) {
            const d = new Date(date)
            const day = d.getDay() || 7
            d.setDate(d.getDate() - day + 1)
            return d
        },
        getWeekEnd(date) {
            const start = this.getWeekStart(date)
            const end = new Date(start)
            end.setDate(start.getDate() + 6)
            return end
        },
        formatDate(date) {
            if (!date) return ''
            const mm = String(date.getMonth() + 1).padStart(2, '0')
            const dd = String(date.getDate()).padStart(2, '0')
            const yyyy = date.getFullYear()
            const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
             return `${mm}/${dd}/${yyyy}(${dayNames[date.getDay()]})`
        },
    }
}

</script>

<style>
    :root {
        --dp-menu-border-color: transparent;    
        --dp-border-color: transparent;
    }
   .dp__menu_content_wrapper {
        padding: 10px !important;
    }

    .dp__sidebar_left {
        padding: 10px !important;
        border-right: none !important;
    }

    .dp--menu-wrapper {
        border: none !important;
        border-radius: 12px !important;
        box-shadow: 0 10px 24px rgba(0,0,0,.12) !important;
    }
    .dp__sidebar_left {
        border-inline-end: none !important;
    }
    .dp__menu_inner {
        width: 100%;
        height: 100%;
        padding: 12px;
        box-sizing: border-box;
    }
    .dp__calendar_header_item {
        font-weight: 400 !important;
    }
    .dp__arrow_bottom {
        display: none !important;
    }
    .dp__arrow_top {
       display: none !important;
    } 
</style>
<style scoped>
    :deep(.dp__input_wrap),
    :deep(.dp__input) {
       display: none !important;   
    }
    :deep(.dp__menu) {
        border: none !important;
        background: transparent !important;
    }
    :deep(.dp__menu_content_wrapper) {
        border: none !important;                    
        border-radius: 16px !important;
        background: #fff;                          
        box-shadow: 0 10px 24px rgba(0,0,0,.12);
    }
</style>


